import React, { useEffect, useMemo, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const STATUS_META = {
  not_started:            { bg: "var(--bg-2)",         color: "var(--muted)",   label: "Not started" },
  implemented:            { bg: "var(--success-soft)", color: "var(--success)", label: "Implemented" },
  partially_implemented:  { bg: "var(--warn-soft)",    color: "var(--warn)",    label: "Partially implemented" },
  needs_evidence:         { bg: "var(--warn-soft)",    color: "var(--warn)",    label: "Needs evidence" },
  under_review:           { bg: "var(--primary-soft)", color: "var(--primary)", label: "Under review" },
  non_compliant:          { bg: "var(--danger-soft)",  color: "var(--danger)",  label: "Non-compliant" },
  accepted_risk:          { bg: "var(--warn-soft)",    color: "var(--warn)",    label: "Accepted risk" },
  closed:                 { bg: "var(--bg-2)",         color: "var(--muted)",   label: "Closed" },
};

const RISK_META = {
  low:      { bg: "var(--bg-2)",         color: "var(--muted)"   },
  medium:   { bg: "var(--warn-soft)",    color: "var(--warn)"    },
  high:     { bg: "var(--danger-soft)",  color: "var(--danger)"  },
  critical: { bg: "var(--danger-soft)",  color: "var(--danger)"  },
};

const REVIEW_META = {
  draft:        { bg: "var(--bg-2)",         color: "var(--muted)"   },
  submitted:    { bg: "var(--primary-soft)", color: "var(--primary)" },
  under_review: { bg: "var(--warn-soft)",    color: "var(--warn)"    },
  approved:     { bg: "var(--success-soft)", color: "var(--success)" },
  rejected:     { bg: "var(--danger-soft)",  color: "var(--danger)"  },
  expired:      { bg: "var(--danger-soft)",  color: "var(--danger)"  },
};

function Pill({ label, meta = {}, testId }) {
  const s = meta[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
          style={{ background: s.bg, color: s.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label || (label || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyControl = {
  control_id: "", name: "", description: "",
  frameworks: [], regulation_mapping: "",
  owner_email: "", owner_role: "control_owner", department: "",
  frequency: "annual", evidence_required: "",
  risk_rating: "medium", status: "not_started",
  last_tested_date: "", next_review_date: "", notes: "",
};

const emptyAudit = {
  name: "", audit_type: "internal", scope: "",
  frameworks: [], lead_auditor_email: "",
  start_date: "", end_date: "", control_ids: [],
};

const emptyFinding = {
  title: "", description: "", severity: "medium",
  control_id_ref: "", observed_evidence: "",
};

export default function ComplianceControls() {
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ statuses: [], risk_ratings: [], frequencies: [],
                                       frameworks: [], evidence_review_states: [],
                                       audit_stages: [], finding_severities: [] });
  const [library, setLibrary] = useState([]);
  const [dash, setDash] = useState(null);
  const [controls, setControls] = useState([]);
  const [evidences, setEvidences] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fFw, setFFw] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fRisk, setFRisk] = useState("");
  const [fDept, setFDept] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [fReview, setFReview] = useState("");
  const [fEvSearch, setFEvSearch] = useState("");
  const [fEvExp, setFEvExp] = useState(0);

  const [controlModal, setControlModal] = useState(false);
  const [controlForm, setControlForm] = useState(emptyControl);
  const [editingControl, setEditingControl] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [controlSubtab, setControlSubtab] = useState("overview");
  const [controlEvidences, setControlEvidences] = useState([]);

  const [uploadForCid, setUploadForCid] = useState(null);
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadEffective, setUploadEffective] = useState("");
  const [uploadExpiry, setUploadExpiry] = useState("");
  const fileRef = useRef(null);

  const [auditModal, setAuditModal] = useState(false);
  const [auditForm, setAuditForm] = useState(emptyAudit);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditSubtab, setAuditSubtab] = useState("overview");

  const [findingModal, setFindingModal] = useState(false);
  const [findingForm, setFindingForm] = useState(emptyFinding);
  const [findingDetail, setFindingDetail] = useState(null);

  const [gapModal, setGapModal] = useState(false);
  const [gapResult, setGapResult] = useState(null);
  const [gapTarget, setGapTarget] = useState("DPDP Act");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, lib, d, c, e, a] = await Promise.all([
        api.get("/compliance-controls-meta/config"),
        api.get("/compliance-controls-meta/framework-library"),
        api.get("/compliance-controls-dashboard"),
        api.get("/compliance-controls", { params: { limit: 500, framework: fFw, status: fStatus, risk_rating: fRisk, department: fDept, search: fSearch } }),
        api.get("/compliance-controls-evidence-list", { params: { limit: 500, review_state: fReview, search: fEvSearch, expiring_in_days: fEvExp } }),
        api.get("/compliance-audits", { params: { limit: 500 } }),
      ]);
      setMeta(m.data); setLibrary(lib.data.items || []);
      setDash(d.data); setControls(c.data.items || []);
      setEvidences(e.data.items || []); setAudits(a.data.items || []);
    } catch (err) { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { loadAll();   }, []);
  useEffect(() => { if (tab === "controls") loadAll();   }, [fFw, fStatus, fRisk, fDept, fSearch]);
  useEffect(() => { if (tab === "evidence") loadAll();   }, [fReview, fEvSearch, fEvExp]);

  // ---------- CONTROLS
  const saveControl = async () => {
    try {
      if (editingControl) {
        await api.put(`/compliance-controls/${editingControl}`, controlForm);
        toast.success("Control updated");
      } else {
        await api.post("/compliance-controls", controlForm);
        toast.success("Control created");
      }
      setControlModal(false); setEditingControl(null); setControlForm(emptyControl);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEditControl = (c) => {
    setControlForm({ ...emptyControl, ...c, frameworks: c.frameworks || [] });
    setEditingControl(c.id); setControlModal(true);
  };
  const removeControl = async (id) => {
    if (!window.confirm("Delete this control?")) return;
    try { await api.delete(`/compliance-controls/${id}`); toast.success("Deleted"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openControl = async (c) => {
    setSelectedControl(c); setControlSubtab("overview");
    try {
      const { data } = await api.get(`/compliance-controls/${c.id}/evidence`);
      setControlEvidences(data.items || []);
    } catch { setControlEvidences([]); }
  };
  const adoptFromLibrary = async (key) => {
    try {
      await api.post("/compliance-controls-adopt-from-library", { key });
      toast.success("Adopted");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadEvidenceForControl = async (cid) => {
    if (!fileRef.current?.files?.[0]) return toast.error("Select a file first");
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    fd.append("description", uploadDesc || "");
    if (uploadEffective) fd.append("effective_date", uploadEffective);
    if (uploadExpiry) fd.append("expiry_date", uploadExpiry);
    try {
      await api.post(`/compliance-controls/${cid}/evidence`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded");
      setUploadForCid(null); setUploadDesc(""); setUploadEffective(""); setUploadExpiry("");
      if (fileRef.current) fileRef.current.value = "";
      if (selectedControl) openControl(selectedControl);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reviewEvidence = async (eid, state) => {
    const note = window.prompt(`Note for ${state} (optional):`, "") || "";
    try {
      await api.post(`/compliance-controls/evidence/${eid}/review`, { review_state: state, review_note: note });
      toast.success(`Marked ${state}`);
      if (selectedControl) openControl(selectedControl);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiReviewEvidence = async (eid) => {
    toast.info("Running AI review…");
    try {
      const { data } = await api.post(`/compliance-controls/evidence/${eid}/ai-review`);
      toast.success(`AI: ${data.adequacy} (score ${data.score})`);
      if (selectedControl) openControl(selectedControl);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ---------- AUDITS
  const saveAudit = async () => {
    try {
      await api.post("/compliance-audits", auditForm);
      toast.success("Audit created");
      setAuditModal(false); setAuditForm(emptyAudit);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openAudit = async (a) => {
    try {
      const { data } = await api.get(`/compliance-audits/${a.id}`);
      setSelectedAudit(data); setAuditSubtab("overview");
    } catch { toast.error("Failed"); }
  };
  const closeAudit = async () => {
    if (!selectedAudit) return;
    const note = window.prompt("Closure note:", "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/compliance-audits/${selectedAudit.id}/close`,
                                        { closure_note: note });
      toast.success("Audit closed");
      setSelectedAudit(data); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const summarizeAudit = async () => {
    if (!selectedAudit) return;
    toast.info("Summarizing with AI…");
    try {
      const { data } = await api.post(`/compliance-audits/${selectedAudit.id}/ai-summarize`);
      setSelectedAudit(s => ({ ...s, summary_ai: data }));
      toast.success("Summary ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveFinding = async () => {
    if (!selectedAudit) return;
    try {
      await api.post(`/compliance-audits/${selectedAudit.id}/findings`, findingForm);
      toast.success("Finding created");
      setFindingModal(false); setFindingForm(emptyFinding);
      await openAudit(selectedAudit);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const draftCAP = async (fid) => {
    toast.info("Drafting corrective action plan…");
    try {
      const { data } = await api.post(`/compliance-audits/findings/${fid}/ai-corrective-action`);
      setFindingDetail(prev => prev ? { ...prev, ai_cap: data } : prev);
      toast.success("CAP drafted");
      if (selectedAudit) await openAudit(selectedAudit);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateFinding = async (fid, updates) => {
    try {
      await api.put(`/compliance-audits/findings/${fid}`, updates);
      toast.success("Finding updated");
      if (selectedAudit) await openAudit(selectedAudit);
      if (findingDetail) setFindingDetail(fd => ({ ...fd, ...updates }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runGapAnalysis = async () => {
    setGapResult(null);
    setGapModal(true);
    toast.info("Analyzing gaps…");
    try {
      const { data } = await api.post("/compliance-controls-ai-gap-analysis",
                                        { target_framework: gapTarget });
      setGapResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setGapModal(false); }
  };

  const controlsById = useMemo(() =>
    Object.fromEntries(controls.map(c => [c.id, c])), [controls]);

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Compliance Controls & Evidence"
        kicker="Multi-framework control library + audit workflow + AI"
        description="Map DPDP + ISO 27001 + SOC 2 + GDPR requirements to controls, evidence, and audits. AI-assisted adequacy review, gap analysis, and management reporting."
        testId="controls-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={loadAll} data-testid="cc-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            <Btn variant="secondary" onClick={runGapAnalysis} data-testid="cc-gap"><Icons.Search className="w-3.5 h-3.5" /> Gap analysis</Btn>
            <button onClick={() => downloadFile("/compliance-controls-management-report.pdf", "controls_management_report.pdf")}
               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11.5px] font-medium"
               style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="cc-mgmt-pdf">
              <Icons.Download className="w-3.5 h-3.5" /> Management report
            </button>
            <Btn onClick={() => { setControlForm(emptyControl); setEditingControl(null); setControlModal(true); }} data-testid="cc-new-control">
              <Icons.Plus className="w-3.5 h-3.5" /> New control
            </Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "controls", label: "Controls", icon: "ListChecks" },
          { key: "evidence", label: "Evidence", icon: "FolderLock" },
          { key: "audits", label: "Audits", icon: "ClipboardCheck" },
          { key: "library", label: "Library", icon: "BookMarked" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`cc-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent",
                         color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && <DashboardTab dash={dash} controlsById={controlsById} />}
      {tab === "controls" && (
        <ControlsTab
          meta={meta} controls={controls}
          filters={{ fFw, fStatus, fRisk, fDept, fSearch }}
          setFilters={{ setFFw, setFStatus, setFRisk, setFDept, setFSearch }}
          onOpen={openControl} onEdit={openEditControl} onDelete={removeControl}
          onUpload={(cid) => setUploadForCid(cid)}
        />
      )}
      {tab === "evidence" && (
        <EvidenceTab
          meta={meta} evidences={evidences} controlsById={controlsById}
          filters={{ fReview, fEvSearch, fEvExp }}
          setFilters={{ setFReview, setFEvSearch, setFEvExp }}
          onReview={reviewEvidence} onAiReview={aiReviewEvidence}
        />
      )}
      {tab === "audits" && (
        <AuditsTab audits={audits} onOpen={openAudit} onNew={() => setAuditModal(true)} />
      )}
      {tab === "library" && (
        <LibraryTab library={library} onAdopt={adoptFromLibrary} />
      )}

      {/* CONTROL MODAL (create/edit) */}
      <ControlModal
        open={controlModal} onClose={() => { setControlModal(false); setEditingControl(null); }}
        meta={meta} form={controlForm} setForm={setControlForm}
        onSave={saveControl} isEdit={!!editingControl}
      />

      {/* CONTROL DETAIL MODAL */}
      <Dialog open={!!selectedControl} onOpenChange={(v) => !v && setSelectedControl(null)}>
        <DialogContent className="max-w-4xl">
          {selectedControl && (
            <ControlDetail
              control={selectedControl} evidences={controlEvidences} meta={meta}
              subtab={controlSubtab} setSubtab={setControlSubtab}
              onUpload={() => setUploadForCid(selectedControl.id)}
              onReview={reviewEvidence} onAiReview={aiReviewEvidence}
              onEdit={() => { openEditControl(selectedControl); setSelectedControl(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* UPLOAD EVIDENCE MODAL */}
      <Dialog open={!!uploadForCid} onOpenChange={(v) => !v && setUploadForCid(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload evidence</DialogTitle>
            <DialogDescription>Attach a file supporting this control. Up to 25 MB (pdf, docx, txt, png, xlsx, csv, json, xml, zip).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Field label="File"><input ref={fileRef} type="file" data-testid="ev-file" className="text-[12px]" /></Field>
            <Field label="Description"><TextArea data-testid="ev-desc" rows={2} value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Effective date"><TextInput type="date" data-testid="ev-eff" value={uploadEffective} onChange={(e) => setUploadEffective(e.target.value)} /></Field>
              <Field label="Expiry date"><TextInput type="date" data-testid="ev-exp" value={uploadExpiry} onChange={(e) => setUploadExpiry(e.target.value)} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setUploadForCid(null)}>Cancel</Btn>
            <Btn onClick={() => uploadEvidenceForControl(uploadForCid)} data-testid="ev-upload">Upload</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW AUDIT MODAL */}
      <Dialog open={auditModal} onOpenChange={setAuditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New audit plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required className="col-span-2"><TextInput data-testid="a-name" value={auditForm.name} onChange={(e) => setAuditForm({ ...auditForm, name: e.target.value })} /></Field>
            <Field label="Type">
              <SelectInput data-testid="a-type" value={auditForm.audit_type} onChange={(e) => setAuditForm({ ...auditForm, audit_type: e.target.value })}>
                <option value="internal">Internal</option>
                <option value="external">External</option>
                <option value="regulator">Regulator</option>
              </SelectInput>
            </Field>
            <Field label="Lead auditor email"><TextInput data-testid="a-lead" value={auditForm.lead_auditor_email || ""} onChange={(e) => setAuditForm({ ...auditForm, lead_auditor_email: e.target.value })} /></Field>
            <Field label="Frameworks" className="col-span-2">
              <div className="flex flex-wrap gap-1.5">
                {(meta.frameworks || []).map(fw => {
                  const active = (auditForm.frameworks || []).includes(fw);
                  return (
                    <button key={fw} type="button" onClick={() => setAuditForm(f => ({
                      ...f, frameworks: active ? f.frameworks.filter(x => x !== fw)
                                                : [...(f.frameworks || []), fw],
                    }))} className="px-2 py-1 text-[11px] rounded-md"
                       style={{ background: active ? "var(--primary-soft)" : "var(--bg-2)",
                                  color: active ? "var(--primary)" : "var(--muted)" }}>{fw}</button>
                  );
                })}
              </div>
            </Field>
            <Field label="Scope" className="col-span-2"><TextArea data-testid="a-scope" rows={2} value={auditForm.scope || ""} onChange={(e) => setAuditForm({ ...auditForm, scope: e.target.value })} /></Field>
            <Field label="Start date"><TextInput type="date" data-testid="a-start" value={auditForm.start_date || ""} onChange={(e) => setAuditForm({ ...auditForm, start_date: e.target.value })} /></Field>
            <Field label="End date"><TextInput type="date" data-testid="a-end" value={auditForm.end_date || ""} onChange={(e) => setAuditForm({ ...auditForm, end_date: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setAuditModal(false)}>Cancel</Btn>
            <Btn onClick={saveAudit} disabled={!auditForm.name} data-testid="a-save">Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUDIT DETAIL MODAL */}
      <Dialog open={!!selectedAudit} onOpenChange={(v) => !v && setSelectedAudit(null)}>
        <DialogContent className="max-w-4xl">
          {selectedAudit && (
            <AuditDetail
              audit={selectedAudit} controlsById={controlsById} meta={meta}
              subtab={auditSubtab} setSubtab={setAuditSubtab}
              onNewFinding={() => { setFindingForm(emptyFinding); setFindingModal(true); }}
              onOpenFinding={(f) => setFindingDetail(f)}
              onSummarize={summarizeAudit}
              onClose={closeAudit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* NEW FINDING MODAL */}
      <Dialog open={findingModal} onOpenChange={setFindingModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>New finding</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Title" required><TextInput data-testid="f-title" value={findingForm.title} onChange={(e) => setFindingForm({ ...findingForm, title: e.target.value })} /></Field>
            <Field label="Severity">
              <SelectInput data-testid="f-sev" value={findingForm.severity} onChange={(e) => setFindingForm({ ...findingForm, severity: e.target.value })}>
                {(meta.finding_severities || []).map(s => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
            </Field>
            <Field label="Related control (optional)">
              <SelectInput data-testid="f-ctrl" value={findingForm.control_id_ref || ""} onChange={(e) => setFindingForm({ ...findingForm, control_id_ref: e.target.value })}>
                <option value="">—</option>
                {controls.map(c => <option key={c.id} value={c.id}>{c.control_id} — {c.name}</option>)}
              </SelectInput>
            </Field>
            <Field label="Description"><TextArea data-testid="f-desc" rows={3} value={findingForm.description || ""} onChange={(e) => setFindingForm({ ...findingForm, description: e.target.value })} /></Field>
            <Field label="Observed evidence"><TextArea data-testid="f-obs" rows={2} value={findingForm.observed_evidence || ""} onChange={(e) => setFindingForm({ ...findingForm, observed_evidence: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setFindingModal(false)}>Cancel</Btn>
            <Btn onClick={saveFinding} disabled={!findingForm.title} data-testid="f-save">Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FINDING DETAIL MODAL */}
      <Dialog open={!!findingDetail} onOpenChange={(v) => !v && setFindingDetail(null)}>
        <DialogContent className="max-w-2xl">
          {findingDetail && (
            <FindingDetail
              finding={findingDetail} meta={meta} controlsById={controlsById}
              onUpdate={(u) => updateFinding(findingDetail.id, u)}
              onDraftCAP={() => draftCAP(findingDetail.id)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* GAP ANALYSIS MODAL */}
      <Dialog open={gapModal} onOpenChange={setGapModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Gap analysis · {gapTarget}</DialogTitle>
            <DialogDescription>
              <SelectInput className="mt-2 max-w-xs" value={gapTarget} onChange={(e) => setGapTarget(e.target.value)} data-testid="gap-target">
                <option value="All">All frameworks</option>
                {(meta.frameworks || []).map(f => <option key={f} value={f}>{f}</option>)}
              </SelectInput>
            </DialogDescription>
          </DialogHeader>
          {gapResult ? (
            <div className="max-h-[60vh] overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-md" style={{ background: "var(--primary-soft)" }}>
                  <div className="text-[10.5px] muted uppercase tracking-wider">Score</div>
                  <div className="text-2xl font-semibold" style={{ color: "var(--primary)" }} data-testid="gap-score">{gapResult.score}%</div>
                </div>
                <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[10.5px] muted uppercase tracking-wider">Maturity</div>
                  <div className="text-2xl font-semibold ink capitalize">{gapResult.overall_maturity}</div>
                </div>
              </div>
              {gapResult.narrative && (
                <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[10.5px] muted uppercase tracking-wider mb-1">Narrative</div>
                  <div className="text-[12px] ink whitespace-pre-wrap">{gapResult.narrative}</div>
                </div>
              )}
              {(gapResult.critical_gaps || []).length > 0 && (
                <div>
                  <div className="text-[12px] font-semibold ink mb-1">Critical gaps</div>
                  <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{gapResult.critical_gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
                </div>
              )}
              {(gapResult.quick_wins || []).length > 0 && (
                <div>
                  <div className="text-[12px] font-semibold ink mb-1">Quick wins</div>
                  <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{gapResult.quick_wins.map((g, i) => <li key={i}>{g}</li>)}</ul>
                </div>
              )}
              {(gapResult.recommended_controls || []).length > 0 && (
                <div>
                  <div className="text-[12px] font-semibold ink mb-1">Recommended controls to add</div>
                  <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{gapResult.recommended_controls.map((g, i) => <li key={i}>{g}</li>)}</ul>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 gap-3">
              <Icons.Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--primary)" }} />
              <span className="text-[12px] muted">Analyzing your control library with AI…</span>
            </div>
          )}
          <DialogFooter>
            <Btn variant="secondary" onClick={runGapAnalysis} data-testid="gap-rerun">Re-run</Btn>
            <Btn onClick={() => setGapModal(false)}>Close</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
// SUB-COMPONENTS
// =====================================================================

function DashboardTab({ dash, controlsById }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Compliance score" value={`${dash.compliance_score}%`} tone={dash.compliance_score < 50 ? "danger" : dash.compliance_score < 80 ? "warn" : "success"} testId="stat-score" />
        <StatCard label="Implementation" value={`${dash.implementation_pct}%`} tone="primary" testId="stat-impl" hint={`${dash.total_controls} controls`} />
        <StatCard label="Open findings" value={dash.open_findings} tone={dash.open_findings > 0 ? "warn" : "success"} testId="stat-findings" />
        <StatCard label="Overdue evidence" value={dash.overdue_evidence} tone={dash.overdue_evidence > 0 ? "danger" : "success"} testId="stat-overdue" />
        <StatCard label="High-risk open" value={dash.high_risk_open} tone={dash.high_risk_open > 0 ? "danger" : "success"} testId="stat-hro" />
        <StatCard label="Open audits" value={dash.open_audits} tone="primary" testId="stat-audits" />
        <StatCard label="Evidence artefacts" value={dash.total_evidence} testId="stat-ev-total" />
        <StatCard label="Upcoming reviews" value={dash.upcoming_reviews?.length || 0} tone="warn" testId="stat-upcoming" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">By framework</div>
          <div className="space-y-2" data-testid="by-framework">
            {dash.by_framework.filter(f => f.total > 0).map(f => (
              <div key={f.framework}>
                <div className="flex items-center text-[11.5px]">
                  <span className="flex-1 ink font-medium">{f.framework}</span>
                  <span className="muted">{f.implemented}/{f.total} · <b style={{ color: "var(--primary)" }}>{f.score}%</b></span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden mt-0.5" style={{ background: "var(--bg-2)" }}>
                  <div className="h-full" style={{ width: `${f.score}%`, background: "var(--primary)" }} />
                </div>
              </div>
            ))}
            {dash.by_framework.filter(f => f.total > 0).length === 0 && <div className="text-[12px] muted">No framework mapping yet. Adopt controls from the library tab.</div>}
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">By department</div>
          <div className="space-y-2" data-testid="by-department">
            {dash.by_department.length === 0 ? <div className="text-[12px] muted">No department mapping yet.</div>
              : dash.by_department.map(d => (
                <div key={d.department}>
                  <div className="flex items-center text-[11.5px]">
                    <span className="flex-1 ink font-medium">{d.department}</span>
                    <span className="muted">{d.implemented}/{d.total}</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden mt-0.5" style={{ background: "var(--bg-2)" }}>
                    <div className="h-full" style={{ width: `${Math.round(d.implemented/Math.max(1,d.total)*100)}%`, background: "var(--success)" }} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">By status</div>
          <div className="space-y-1.5" data-testid="by-status">
            {Object.entries(dash.by_status).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-[11.5px]">
                <Pill label={k} meta={STATUS_META} />
                <div className="flex-1" /><div className="muted">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3">Findings by severity</div>
          <div className="space-y-1.5" data-testid="findings-by-severity">
            {Object.entries(dash.findings_by_severity).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-[11.5px]">
                <span className="w-2 h-2 rounded-full" style={{ background: (RISK_META[k] || {}).color }} />
                <span className="ink capitalize flex-1">{k}</span>
                <span className="muted">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3 flex items-center gap-2">
            <Icons.Clock className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />
            Upcoming control reviews
          </div>
          <div className="space-y-1.5" data-testid="upcoming-reviews">
            {(dash.upcoming_reviews || []).length === 0 ? <div className="text-[12px] muted">No upcoming reviews.</div>
              : (dash.upcoming_reviews || []).map(r => (
                <div key={r.id} className="p-2 rounded-md flex items-center gap-2" style={{ background: "var(--bg-2)" }}>
                  <span className="text-[11px] mono ink">{r.control_id}</span>
                  <span className="text-[11.5px] ink flex-1 truncate">{r.name}</span>
                  <span className="text-[11px]" style={{ color: r.days_delta < 0 ? "var(--danger)" : "var(--warn)" }}>
                    {r.days_delta < 0 ? `${-r.days_delta}d overdue` : `in ${r.days_delta}d`}
                  </span>
                </div>
              ))}
          </div>
        </div>
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] font-semibold ink mb-3 flex items-center gap-2">
            <Icons.AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--danger)" }} />
            Top open findings
          </div>
          <div className="space-y-1.5" data-testid="top-findings">
            {(dash.top_open_findings || []).length === 0 ? <div className="text-[12px] muted">No open findings. 🎯</div>
              : (dash.top_open_findings || []).map(f => (
                <div key={f.id} className="p-2 rounded-md flex items-center gap-2" style={{ background: "var(--bg-2)" }}>
                  <Pill label={f.severity} meta={RISK_META} />
                  <div className="text-[11.5px] ink flex-1 truncate">{f.title}</div>
                  {f.control_id_ref && controlsById[f.control_id_ref] && (
                    <span className="text-[10px] muted mono">{controlsById[f.control_id_ref].control_id}</span>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ControlsTab({ meta, controls, filters, setFilters, onOpen, onEdit, onDelete, onUpload }) {
  const { fFw, fStatus, fRisk, fDept, fSearch } = filters;
  const { setFFw, setFStatus, setFRisk, setFDept, setFSearch } = setFilters;
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        <TextInput data-testid="cc-search" placeholder="Search ID / name / regulation" value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
        <SelectInput data-testid="cc-f-fw" value={fFw} onChange={(e) => setFFw(e.target.value)}>
          <option value="">All frameworks</option>
          {(meta.frameworks || []).map(f => <option key={f} value={f}>{f}</option>)}
        </SelectInput>
        <SelectInput data-testid="cc-f-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(meta.statuses || []).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </SelectInput>
        <SelectInput data-testid="cc-f-risk" value={fRisk} onChange={(e) => setFRisk(e.target.value)}>
          <option value="">All risks</option>
          {(meta.risk_ratings || []).map(r => <option key={r} value={r}>{r}</option>)}
        </SelectInput>
        <TextInput data-testid="cc-f-dept" placeholder="Department" value={fDept} onChange={(e) => setFDept(e.target.value)} />
      </div>
      {controls.length === 0 ? <EmptyState title="No controls yet" hint="Adopt from the framework library or click 'New control'." />
        : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["ID", "Name", "Frameworks", "Owner", "Risk", "Status", "Next review", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="controls-tbody">
                {controls.map(c => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`ctrl-row-${c.id}`} className="hover:bg-app cursor-pointer" onClick={() => onOpen(c)}>
                    <td className="px-4 py-2 text-[11px] mono ink">{c.control_id}</td>
                    <td className="px-4 py-2 text-[12px] ink font-medium truncate max-w-xs">{c.name}
                      {c.regulation_mapping && <div className="text-[10px] muted mt-0.5 truncate">{c.regulation_mapping}</div>}
                    </td>
                    <td className="px-4 py-2 text-[10.5px]">
                      <div className="flex flex-wrap gap-1">
                        {(c.frameworks || []).map(f => <span key={f} className="px-1.5 py-[1px] rounded" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>{f}</span>)}
                        {(c.frameworks || []).length === 0 && <span className="muted">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-[11px] muted truncate max-w-[140px]">{c.owner_email || "—"}<br /><span className="text-[10px]">{c.department || ""}</span></td>
                    <td className="px-4 py-2"><Pill label={c.risk_rating} meta={RISK_META} /></td>
                    <td className="px-4 py-2"><Pill label={c.status} meta={STATUS_META} /></td>
                    <td className="px-4 py-2 text-[10.5px] muted">{c.next_review_date || "—"}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onUpload(c.id)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`ctrl-upload-${c.id}`}>Upload</button>
                      <button onClick={() => onEdit(c)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`ctrl-edit-${c.id}`}>Edit</button>
                      <button onClick={() => onDelete(c.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`ctrl-del-${c.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function EvidenceTab({ meta, evidences, controlsById, filters, setFilters, onReview, onAiReview }) {
  const { fReview, fEvSearch, fEvExp } = filters;
  const { setFReview, setFEvSearch, setFEvExp } = setFilters;
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <TextInput data-testid="ev-search" placeholder="Search filename / description / owner" value={fEvSearch} onChange={(e) => setFEvSearch(e.target.value)} />
        <SelectInput data-testid="ev-f-state" value={fReview} onChange={(e) => setFReview(e.target.value)}>
          <option value="">All review states</option>
          {(meta.evidence_review_states || []).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </SelectInput>
        <SelectInput data-testid="ev-f-exp" value={fEvExp} onChange={(e) => setFEvExp(Number(e.target.value))}>
          <option value={0}>All expiry windows</option>
          <option value={30}>Expiring in 30 days</option>
          <option value={90}>Expiring in 90 days</option>
        </SelectInput>
      </div>
      {evidences.length === 0 ? <EmptyState title="No evidence uploaded" hint="Upload evidence from a control." />
        : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["File", "Control", "Owner", "Effective", "Expiry", "v", "State", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="evidences-tbody">
                {evidences.map(e => {
                  const ctrl = e.control_id_ref ? controlsById[e.control_id_ref] : null;
                  return (
                    <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`ev-row-${e.id}`}>
                      <td className="px-4 py-2 text-[11.5px] ink font-medium truncate max-w-xs">{e.filename}
                        {e.description && <div className="text-[10px] muted mt-0.5 truncate">{e.description}</div>}
                      </td>
                      <td className="px-4 py-2 text-[10.5px] mono muted">{ctrl?.control_id || (e.orphan ? <span style={{ color: "var(--danger)" }}>orphan</span> : "—")}</td>
                      <td className="px-4 py-2 text-[11px] muted truncate">{e.owner_email}</td>
                      <td className="px-4 py-2 text-[10.5px] muted">{e.effective_date || "—"}</td>
                      <td className="px-4 py-2 text-[10.5px] muted">{e.expiry_date || "—"}</td>
                      <td className="px-4 py-2 text-[10.5px] mono">v{e.version}</td>
                      <td className="px-4 py-2"><Pill label={e.review_state} meta={REVIEW_META} /></td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => downloadFile(`/compliance-controls/evidence/${e.id}/download`, e.filename)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`ev-dl-${e.id}`}>Download</button>
                        <button onClick={() => onAiReview(e.id)} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }} data-testid={`ev-ai-${e.id}`}>AI review</button>
                        <button onClick={() => onReview(e.id, "approved")} className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--success)" }} data-testid={`ev-approve-${e.id}`}>Approve</button>
                        <button onClick={() => onReview(e.id, "rejected")} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`ev-reject-${e.id}`}>Reject</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function AuditsTab({ audits, onOpen, onNew }) {
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Btn size="sm" onClick={onNew} data-testid="audit-new"><Icons.Plus className="w-3 h-3" /> New audit</Btn>
      </div>
      {audits.length === 0 ? <EmptyState title="No audits yet" hint="Create your first audit plan." />
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="audits-grid">
            {audits.map(a => (
              <div key={a.id} className="surface rounded-xl p-4 cursor-pointer hover:shadow-sm transition-shadow" style={{ border: "1px solid var(--border)" }} onClick={() => onOpen(a)} data-testid={`audit-card-${a.id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    <Icons.ClipboardCheck className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold ink truncate">{a.name}</div>
                    <div className="text-[10.5px] muted capitalize">{a.audit_type} · {(a.frameworks || []).join(", ") || "—"}</div>
                  </div>
                  <Pill label={a.stage} />
                </div>
                {a.scope && <div className="text-[11px] muted line-clamp-2 mb-2">{a.scope}</div>}
                <div className="flex items-center gap-3 text-[10.5px] muted">
                  {a.start_date && <span><Icons.Calendar className="inline w-3 h-3 mr-0.5" />{a.start_date}</span>}
                  <span className="ml-auto">{(a.control_tests || []).length} tests</span>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function LibraryTab({ library, onAdopt }) {
  const groups = useMemo(() => {
    const g = {};
    library.forEach(l => { g[l.framework] = g[l.framework] || []; g[l.framework].push(l); });
    return g;
  }, [library]);
  return (
    <div>
      <div className="text-[12px] muted mb-3">One-click adopt pre-built controls mapped to DPDP Act, ISO 27001, SOC 2, and GDPR into your library.</div>
      {Object.entries(groups).map(([fw, items]) => (
        <div key={fw} className="mb-5">
          <div className="text-[13px] font-semibold ink mb-2 flex items-center gap-2">
            <span className="px-2 py-0.5 rounded text-[11px]" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>{fw}</span>
            <span className="muted text-[11px]">{items.length} controls</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`lib-${fw.replace(/[^a-z0-9]/gi, "-")}`}>
            {items.map(l => (
              <div key={l.key} className="surface rounded-xl p-3" style={{ border: "1px solid var(--border)" }}>
                <div className="flex items-start gap-2 mb-1">
                  <div className="text-[10.5px] mono muted shrink-0">{l.control_id}</div>
                  <div className="text-[12.5px] font-medium ink flex-1">{l.name}</div>
                  <Pill label={l.risk_rating} meta={RISK_META} />
                </div>
                <div className="text-[11px] muted mb-2 line-clamp-2">{l.description}</div>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] muted mono flex-1">{l.regulation}</div>
                  <Btn size="sm" variant="secondary" onClick={() => onAdopt(l.key)} data-testid={`adopt-${l.key}`}><Icons.PlusCircle className="w-3 h-3" /> Adopt</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ControlModal({ open, onClose, meta, form, setForm, onSave, isEdit }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit control" : "New control"}</DialogTitle>
          <DialogDescription>Define control metadata, ownership, and testing cadence.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto">
          <Field label="Control ID" required>
            <TextInput data-testid="cc-cid" value={form.control_id} disabled={isEdit} onChange={(e) => setForm({ ...form, control_id: e.target.value })} placeholder="e.g., DPDP-8.7" />
          </Field>
          <Field label="Frequency">
            <SelectInput data-testid="cc-freq" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              {(meta.frequencies || []).map(f => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
            </SelectInput>
          </Field>
          <Field label="Name" required className="col-span-2"><TextInput data-testid="cc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description" className="col-span-2"><TextArea data-testid="cc-desc" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Frameworks" className="col-span-2">
            <div className="flex flex-wrap gap-1.5">
              {(meta.frameworks || []).map(fw => {
                const active = (form.frameworks || []).includes(fw);
                return (
                  <button key={fw} type="button" data-testid={`cc-fw-${fw.replace(/[^a-z0-9]/gi, "")}`}
                    onClick={() => setForm(f => ({ ...f, frameworks: active ? f.frameworks.filter(x => x !== fw) : [...(f.frameworks || []), fw] }))}
                    className="px-2 py-1 text-[11px] rounded-md"
                    style={{ background: active ? "var(--primary-soft)" : "var(--bg-2)", color: active ? "var(--primary)" : "var(--muted)" }}>{fw}</button>
                );
              })}
            </div>
          </Field>
          <Field label="Regulation mapping"><TextInput data-testid="cc-reg" value={form.regulation_mapping || ""} onChange={(e) => setForm({ ...form, regulation_mapping: e.target.value })} placeholder="e.g., DPDP Sec. 8(7)" /></Field>
          <Field label="Risk rating">
            <SelectInput data-testid="cc-risk" value={form.risk_rating} onChange={(e) => setForm({ ...form, risk_rating: e.target.value })}>
              {(meta.risk_ratings || []).map(r => <option key={r} value={r}>{r}</option>)}
            </SelectInput>
          </Field>
          <Field label="Owner email"><TextInput data-testid="cc-owner" value={form.owner_email || ""} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></Field>
          <Field label="Department"><TextInput data-testid="cc-dept" value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
          <Field label="Status">
            <SelectInput data-testid="cc-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {(meta.statuses || []).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </SelectInput>
          </Field>
          <Field label="Last tested"><TextInput type="date" data-testid="cc-last" value={form.last_tested_date || ""} onChange={(e) => setForm({ ...form, last_tested_date: e.target.value })} /></Field>
          <Field label="Next review"><TextInput type="date" data-testid="cc-next" value={form.next_review_date || ""} onChange={(e) => setForm({ ...form, next_review_date: e.target.value })} /></Field>
          <Field label="Evidence required" className="col-span-2"><TextArea data-testid="cc-evreq" rows={2} value={form.evidence_required || ""} onChange={(e) => setForm({ ...form, evidence_required: e.target.value })} /></Field>
          <Field label="Notes" className="col-span-2"><TextArea data-testid="cc-notes" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={onSave} disabled={!form.control_id || !form.name} data-testid="cc-save">{isEdit ? "Update" : "Create"}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ControlDetail({ control, evidences, subtab, setSubtab, onUpload, onReview, onAiReview, onEdit }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className="mono text-[13px]">{control.control_id}</span>
          <span>{control.name}</span>
        </DialogTitle>
        <DialogDescription className="flex items-center gap-2 flex-wrap">
          <Pill label={control.status} meta={STATUS_META} />
          <Pill label={control.risk_rating} meta={RISK_META} />
          {(control.frameworks || []).map(f => <span key={f} className="px-1.5 py-[1px] text-[10px] rounded" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>{f}</span>)}
        </DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-1 mb-3">
        {[["overview", "Overview"], ["evidence", "Evidence"], ["activity", "Activity"]].map(([k, l]) => (
          <button key={k} data-testid={`cc-sub-${k}`} onClick={() => setSubtab(k)}
            className="px-2.5 py-1 text-[11px] font-medium rounded-md"
            style={{ background: subtab === k ? "var(--primary-soft)" : "transparent", color: subtab === k ? "var(--primary)" : "var(--muted)" }}>{l}</button>
        ))}
        <div className="flex-1" />
        <Btn size="sm" variant="secondary" onClick={onEdit} data-testid="cc-edit-btn"><Icons.Edit className="w-3 h-3" /> Edit</Btn>
        <Btn size="sm" onClick={onUpload} data-testid="cc-upload-btn"><Icons.Upload className="w-3 h-3" /> Upload evidence</Btn>
      </div>
      <div className="max-h-[55vh] overflow-y-auto">
        {subtab === "overview" && (
          <div className="grid grid-cols-2 gap-3">
            {[["Regulation", control.regulation_mapping],
               ["Owner", control.owner_email],
               ["Department", control.department],
               ["Frequency", (control.frequency || "").replace(/_/g, " ")],
               ["Last tested", control.last_tested_date],
               ["Next review", control.next_review_date]].map(([k, v]) => (
              <div key={k} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider muted">{k}</div>
                <div className="text-[11.5px] ink mt-0.5">{v || "—"}</div>
              </div>
            ))}
            <div className="col-span-2 p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-1">Description</div>
              <div className="text-[12px] ink whitespace-pre-wrap">{control.description || "—"}</div>
            </div>
            <div className="col-span-2 p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-1">Evidence required</div>
              <div className="text-[12px] ink whitespace-pre-wrap">{control.evidence_required || "—"}</div>
            </div>
          </div>
        )}
        {subtab === "evidence" && (
          evidences.length === 0
            ? <EmptyState title="No evidence yet" hint="Click 'Upload evidence' above." />
            : <div className="space-y-2">{evidences.map(e => (
              <div key={e.id} className="surface rounded-md p-3" style={{ border: "1px solid var(--border)" }}>
                <div className="flex items-start gap-2 mb-1">
                  <Icons.FileText className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium ink truncate">{e.filename} <span className="text-[10px] muted mono">v{e.version}</span></div>
                    {e.description && <div className="text-[10.5px] muted mt-0.5">{e.description}</div>}
                    <div className="text-[10px] muted mt-0.5">
                      Uploaded {e.uploaded_at?.slice(0,10)} by {e.uploaded_by} · Effective {e.effective_date || "—"} · Expiry {e.expiry_date || "—"}
                    </div>
                  </div>
                  <Pill label={e.review_state} meta={REVIEW_META} />
                </div>
                {e.ai_review && (
                  <div className="mt-2 p-2 rounded-md" style={{ background: "var(--primary-soft)" }}>
                    <div className="text-[10.5px] font-semibold" style={{ color: "var(--primary)" }}>AI review · {e.ai_review.adequacy} (score {e.ai_review.score})</div>
                    <div className="text-[11px] ink mt-1 whitespace-pre-wrap">{e.ai_review.reasoning}</div>
                    {(e.ai_review.gaps || []).length > 0 && (
                      <div className="mt-1 text-[10.5px]"><b>Gaps:</b> {e.ai_review.gaps.join(" · ")}</div>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => downloadFile(`/compliance-controls/evidence/${e.id}/download`, e.filename)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }}>Download</button>
                  <button onClick={() => onAiReview(e.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }}>AI review</button>
                  <div className="flex-1" />
                  <Btn size="sm" variant="secondary" onClick={() => onReview(e.id, "approved")} data-testid={`sub-ev-approve-${e.id}`}>Approve</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => onReview(e.id, "rejected")} data-testid={`sub-ev-reject-${e.id}`}>Reject</Btn>
                </div>
              </div>
            ))}</div>
        )}
        {subtab === "activity" && (
          <div className="space-y-1.5">
            {(control.activity_log || []).slice().reverse().map(ev => (
              <div key={ev.id} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                <div className="text-[11.5px] ink"><b>{(ev.action || "").replace(/_/g, " ")}</b></div>
                <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function AuditDetail({ audit, controlsById, meta, subtab, setSubtab, onNewFinding, onOpenFinding, onSummarize, onClose }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{audit.name}</DialogTitle>
        <DialogDescription className="flex items-center gap-2">
          <Pill label={audit.stage} />
          <span className="text-[11px] muted">{audit.audit_type} · {(audit.frameworks || []).join(", ")}</span>
          <span className="ml-auto text-[11px] muted">{(audit.findings || []).length} findings</span>
        </DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-1 mb-3">
        {[["overview", "Overview"], ["findings", "Findings"], ["ai", "AI summary"]].map(([k, l]) => (
          <button key={k} data-testid={`a-sub-${k}`} onClick={() => setSubtab(k)}
            className="px-2.5 py-1 text-[11px] font-medium rounded-md"
            style={{ background: subtab === k ? "var(--primary-soft)" : "transparent", color: subtab === k ? "var(--primary)" : "var(--muted)" }}>{l}</button>
        ))}
        <div className="flex-1" />
        <button onClick={() => downloadFile(`/compliance-audits/${audit.id}/report.pdf`, `audit_${audit.id.slice(0, 8)}.pdf`)} className="text-[11px] font-medium px-2 py-1 rounded" style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="a-pdf">
          <Icons.Download className="inline w-3 h-3 mr-1" />PDF
        </button>
        {audit.status !== "closed" && <Btn size="sm" variant="secondary" onClick={onClose} data-testid="a-close">Close audit</Btn>}
      </div>
      <div className="max-h-[55vh] overflow-y-auto">
        {subtab === "overview" && (
          <div className="grid grid-cols-2 gap-3">
            {[["Type", audit.audit_type], ["Frameworks", (audit.frameworks || []).join(", ")],
               ["Lead auditor", audit.lead_auditor_email], ["Stage", audit.stage],
               ["Start", audit.start_date], ["End", audit.end_date]].map(([k, v]) => (
              <div key={k} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider muted">{k}</div>
                <div className="text-[11.5px] ink mt-0.5 capitalize">{v || "—"}</div>
              </div>
            ))}
            <div className="col-span-2 p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-1">Scope</div>
              <div className="text-[12px] ink whitespace-pre-wrap">{audit.scope || "—"}</div>
            </div>
          </div>
        )}
        {subtab === "findings" && (
          <div>
            <div className="mb-3 flex justify-end">
              <Btn size="sm" onClick={onNewFinding} data-testid="a-new-finding"><Icons.Plus className="w-3 h-3" /> New finding</Btn>
            </div>
            {(audit.findings || []).length === 0 ? <EmptyState title="No findings" hint="Click 'New finding' to add one." />
              : (
                <div className="space-y-2" data-testid="a-findings-list">
                  {audit.findings.map(f => (
                    <div key={f.id} className="surface rounded-md p-3 cursor-pointer hover:shadow-sm" style={{ border: "1px solid var(--border)" }} onClick={() => onOpenFinding(f)} data-testid={`finding-card-${f.id}`}>
                      <div className="flex items-start gap-2 mb-1">
                        <Pill label={f.severity} meta={RISK_META} />
                        <div className="flex-1 text-[12px] font-medium ink">{f.title}</div>
                        <Pill label={f.status} />
                      </div>
                      {f.description && <div className="text-[11px] muted line-clamp-2">{f.description}</div>}
                      {f.control_id_ref && controlsById[f.control_id_ref] && (
                        <div className="text-[10px] mono muted mt-1">Control: {controlsById[f.control_id_ref].control_id}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
        {subtab === "ai" && (
          <div>
            <div className="mb-3 flex justify-end">
              <Btn size="sm" onClick={onSummarize} data-testid="a-summarize"><Icons.Sparkles className="w-3 h-3" /> {audit.summary_ai ? "Regenerate" : "Summarize"}</Btn>
            </div>
            {!audit.summary_ai ? <EmptyState title="No AI summary yet" hint="Click 'Summarize' to generate one." />
              : (
                <div className="space-y-3">
                  {audit.summary_ai.exec_summary && (
                    <div className="p-3 rounded-md" style={{ background: "var(--primary-soft)" }}>
                      <div className="text-[10.5px] font-semibold" style={{ color: "var(--primary)" }}>Executive summary</div>
                      <div className="text-[12px] ink mt-1 whitespace-pre-wrap">{audit.summary_ai.exec_summary}</div>
                    </div>
                  )}
                  {(audit.summary_ai.themes || []).length > 0 && (
                    <div><div className="text-[11.5px] font-semibold ink mb-1">Themes</div>
                      <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{audit.summary_ai.themes.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
                  )}
                  {(audit.summary_ai.top_risks || []).length > 0 && (
                    <div><div className="text-[11.5px] font-semibold ink mb-1">Top risks</div>
                      <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{audit.summary_ai.top_risks.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
                  )}
                  {(audit.summary_ai.recommendations || []).length > 0 && (
                    <div><div className="text-[11.5px] font-semibold ink mb-1">Recommendations</div>
                      <ul className="text-[11.5px] muted list-disc pl-4 space-y-0.5">{audit.summary_ai.recommendations.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
                  )}
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
}

function FindingDetail({ finding, meta, controlsById, onUpdate, onDraftCAP }) {
  const [status, setStatus] = useState(finding.status);
  const [mgmt, setMgmt] = useState(finding.management_response || "");
  const [closureNote, setClosureNote] = useState(finding.closure_note || "");
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Pill label={finding.severity} meta={RISK_META} />
          <span>{finding.title}</span>
        </DialogTitle>
        {finding.control_id_ref && controlsById[finding.control_id_ref] && (
          <DialogDescription>Related control: <span className="mono">{controlsById[finding.control_id_ref].control_id}</span> — {controlsById[finding.control_id_ref].name}</DialogDescription>
        )}
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto space-y-3">
        <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">Description</div>
          <div className="text-[12px] ink mt-1 whitespace-pre-wrap">{finding.description || "—"}</div>
        </div>
        {finding.observed_evidence && (
          <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
            <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">Observed evidence</div>
            <div className="text-[12px] ink mt-1 whitespace-pre-wrap">{finding.observed_evidence}</div>
          </div>
        )}
        {finding.ai_cap && (
          <div className="p-3 rounded-md" style={{ background: "var(--primary-soft)" }}>
            <div className="text-[10.5px] font-semibold" style={{ color: "var(--primary)" }}>AI Corrective Action Plan</div>
            {finding.ai_cap.root_cause_analysis && (
              <div className="mt-2"><div className="text-[10px] uppercase tracking-wider muted">Root cause</div>
                <div className="text-[11.5px] ink whitespace-pre-wrap mt-0.5">{finding.ai_cap.root_cause_analysis}</div></div>
            )}
            {(finding.ai_cap.corrective_actions || []).length > 0 && (
              <div className="mt-2"><div className="text-[10px] uppercase tracking-wider muted">Actions</div>
                <ul className="text-[11.5px] ink list-disc pl-4 mt-0.5">
                  {finding.ai_cap.corrective_actions.map((a, i) => (
                    <li key={i}><b>{a.action}</b> — {a.owner_role} ({a.target_days}d)</li>
                  ))}
                </ul></div>
            )}
            {finding.ai_cap.management_response_draft && (
              <div className="mt-2"><div className="text-[10px] uppercase tracking-wider muted">Suggested management response</div>
                <div className="text-[11.5px] ink whitespace-pre-wrap mt-0.5">{finding.ai_cap.management_response_draft}</div></div>
            )}
          </div>
        )}
        <Field label="Status">
          <SelectInput data-testid="fd-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {(meta.finding_statuses || []).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </SelectInput>
        </Field>
        <Field label="Management response">
          <TextArea data-testid="fd-mgmt" rows={3} value={mgmt} onChange={(e) => setMgmt(e.target.value)} />
        </Field>
        <Field label="Closure note">
          <TextArea data-testid="fd-close" rows={2} value={closureNote} onChange={(e) => setClosureNote(e.target.value)} />
        </Field>
      </div>
      <DialogFooter>
        <Btn variant="secondary" onClick={onDraftCAP} data-testid="fd-cap"><Icons.Sparkles className="w-3 h-3" /> Draft CAP with AI</Btn>
        <Btn onClick={() => onUpdate({ status, management_response: mgmt, closure_note: closureNote })} data-testid="fd-save">Save</Btn>
      </DialogFooter>
    </>
  );
}
